import * as R from 'ramda';
import React from 'react';
import {withRouter} from 'react-router-dom';
import {compose} from 'recompose';

import {translate} from 'iss/strings';
import {createActivations} from 'iss/service/EvaluationActivationService';
import {withLocalStorage} from 'iss/utils/withLocalStorage';
import {AccordionForm, AccordionFormSection as Section} from 'iss/widgets/AccordionForm';
import {Button} from 'iss/widgets/Buttons';
import {Checkbox} from 'iss/widgets/Forms';
import {PlainRestResultMessage, SuccessBox} from 'iss/widgets/MessageBox';
import {EvaluationTypeSelector, SplittingContractSelector, toSplitContracts} from 'iss/widgets/Selectors';
import {FormSelectorSection, ServiceSelectorSection} from 'iss/widgets/activation/ActivatorSections';
import {RecurrenceDatesSelector, toActivationRecurrenceDates} from 'iss/widgets/activation/DateSelectorSections';
import {MultiPartySelectorSection, getActivationParties} from 'iss/widgets/activation/MultiPartySelectorSection';
import {MultiProfitCenterSelector} from 'iss/widgets/activation/MultiProfitCenterSelector';
import {BatchOpResultsDialog} from 'iss/widgets/batchOps/BatchOpResultsDialog';
import Modal from 'iss/widgets/bootstrap/Modal';
import {formatLocationAddress, formatSiteAddress} from 'iss/widgets/format/Site';
import validators from 'iss/widgets/form/validation/validators';

const emptyActivations = {
  contracts: [],
  unsplitContracts: [],
  submittedContracts: [],
  evaluationType: 'REGULAR_EVALUATION',
  recurrenceType: 'MONTHLY',
  recurrenceDates: {},
  parties: {},
  sendPdfReport: true
};

const BatchEvaluationActivator = compose(
  withRouter,
  withLocalStorage(
    "batchActivatorState-2.2",
    "activatorState", "setActivatorState", "clearActivatorState",
    emptyActivations,
  )
)(class BatchEvaluationActivator extends React.Component {
  state = {activationResults: null};
  accordionForm = React.createRef();
  updateFormState = (updater) => {
    this.props.setActivatorState(R.compose(this.recomputeSplits, updater));
  };
  recomputeSplits = activation => {
    return R.assoc("contracts", toSplitContracts(activation.unsplitContracts), activation);
  };
  render() {
    const activation = this.props.activatorState;
    const {activationResults} = this.state;

    return <React.Fragment>
      <h1>{translate('batchEvaluationActivator.title')}</h1>
      <AccordionForm ref={this.accordionForm} values={activation}
        valueUpdater={this.updateFormState}
        onSubmit={this.activate} submitButtonText={translate('evaluationActivator.activate')} >
        <ServiceSelectorSection/>
        <FormSelectorSection activation={activation} />
        <Section id='contractChooser' title={translate('batchEvaluationActivator.contractChooserSectionTitle')}>
          <SplittingContractSelector multi={true} label="evaluationActivator.evaluationSite"
            id="contract" valuePath="unsplitContracts" validate={validators.chooseOne}
            hilightContractsWithActivatedForm={R.propOr(null, "id", activation.form)}/>
        </Section>
        <MultiPartySelectorSection/>
        <Section id='profitCenters' title={translate('batchEvaluationActivator.profitCenters')}>
          <MultiProfitCenterSelector/>
        </Section>
        <Section id='settings' title={translate('evaluationActivator.settingsSectionTitle')}>
          <EvaluationTypeSelector label="domain.activatedEvaluation.evaluationType" id="evaluation-type" valuePath="evaluationType" />
          <RecurrenceDatesSelector multi={true} />
          <Checkbox id="hideLogoOnReport" className="text-nowrap"
            label="batchEvaluationActivator.hideLogoOnReport" valuePath="hideLogoOnReport" />
          <Checkbox id="sendPdfReport" className="text-nowrap"
            label="batchEvaluationActivator.sendPdfReport" valuePath="sendPdfReport" />

        </Section>
      </AccordionForm>
      <BatchOpResultsDialog operationResults={activationResults}
        size="large" title="batchEvaluationActivator.activationModalTitle"
        inProgressMessage="batchEvaluationActivator.sendingActivations"
        renderSuccess={<ActivationSuccessful onExitDialog={this.clearSavedState} />}
        renderFailure={
          <ActivationFailed {...activationResults} contracts={activation.submittedContracts}
            onNewActivation={this.clearSavedState}
            onTweakActivationSettings={this.tweakFailed}
            onRetry={this.activate} />
        } />
    </React.Fragment>;
  }
  activate = () => {
    const {evaluationType, recurrenceType, recurrenceDates, service, contracts, form, parties, profitCenters, hideLogoOnReport, sendPdfReport} = this.props.activatorState;
    this.props.setActivatorState({submittedContracts: contracts});
    const activations = contracts.map(contract => ({
      evaluationFormId: form.id,
      contractExternalId: contract.externalId,
      location: contract.location,
      evaluationParties: getActivationParties(contract, parties),
      hideLogoOnReport,
      sendPdfReport,
      recurrenceType,
      profitCenterId: getProfitCenter(profitCenters, contract),
      evaluationType,
      recurrenceDates: toActivationRecurrenceDates({
        recurrenceType,
        recurrenceDates: recurrenceDates[contract.id]
      }),
      serviceTypeId: service,
    }));

    return createActivations(activations).subscribe((results) => {
      this.setState({activationResults: results}, () => {
        if (results.complete && !results.success) {
          this.removeSuccessfulContracts();
        }
      });
    });
  };
  removeSuccessfulContracts = () => {
    const {activationResults} = this.state;

    this.props.setActivatorState((activation) => {
      const failedActivationsByContractExternalId = R.pipe(
        R.pluck('activation'),
        R.groupBy(R.prop('contractExternalId'))
      )(activationResults.failed);

      const unsplitContracts = activation.unsplitContracts
        .filter(contract => R.has(contract.externalId, failedActivationsByContractExternalId))
        .map(contract => {
          const splits = R.pipe(
            R.prop(contract.externalId),
            R.pluck('location'),
            R.reject(R.isNil)
          )(failedActivationsByContractExternalId);
          return R.assoc("splits", splits, contract);
        });

      return {
        ...activation,
        unsplitContracts,
        contracts: toSplitContracts(unsplitContracts)
      };
    });
  };
  tweakFailed = () => {
    this.setState({activationResults: null});
  };
  clearSavedState = () => {
    this.props.setActivatorState(emptyActivations);
    this.setState({activationResults: null});
    this.accordionForm.current.openSection(0);
  };
});

function getProfitCenter(profitCenters, contract) {
  const profitCenter = profitCenters && profitCenters[contract.id];
  return profitCenter ? profitCenter.id : null;
}

function ActivationSuccessful({onExitDialog}) {
  return <Modal.Contents>
    {{
      body: <SuccessBox message={translate('batchEvaluationActivator.activationSuccess')} />,
      footer: <React.Fragment>
        <Button onClick={onExitDialog} id="new-activation">
          {translate('batchEvaluationActivator.newActivation')}
        </Button>
        <Button buttonLook="primary" id="goToActivationList" link="/activatedEvaluations" onClick={onExitDialog}>
          {translate('batchEvaluationActivator.seeActivationList')}
        </Button>
      </React.Fragment>
    }}
  </Modal.Contents>;
}

function ActivationFailed({failed, onNewActivation, onTweakActivationSettings, onRetry, contracts}) {
  const getContract = id => R.defaultTo({}, R.find(R.propEq(id, 'externalId'), contracts));
  return <Modal.Contents>
    {{
      body: <React.Fragment>
        <h1>{translate('batchEvaluationActivator.activationFailed')}</h1>
        <p>{translate('batchEvaluationActivator.theseContractActivationsFailed', {count: failed.length})}</p>
        <table className="table table-striped" id="failedActivationsList">
          <tbody>
            {
              failed.map(({activation, result}) => {
                const contract = getContract(activation.contractExternalId);
                return <tr key={activation.contractExternalId}>
                  <td>
                    <strong>{contract.site.name} {activation.location && activation.location.name}</strong>,
                    {" "}
                    {(activation.location && activation.location.address) ? formatLocationAddress(activation.location) : formatSiteAddress(contract.site)}
                  </td>
                  <td><PlainRestResultMessage result={result} /></td>
                </tr>;
              })
            }
          </tbody>
        </table>
      </React.Fragment>,
      footer: <React.Fragment>
        <p>{translate('batchEvaluationActivator.doYouWantToRetryFailedActivations')}</p>
        <Button id="new-activation" onClick={onNewActivation}>
          {translate('batchEvaluationActivator.whollyNewActivation')}
        </Button>
        <Button id="tweak-activation-settings" buttonLook="primary" onClick={onTweakActivationSettings}>
          {translate('batchEvaluationActivator.changeActivationSettings')}
        </Button>
        <Button buttonLook="danger" onClick={onRetry}>
          {translate('batchEvaluationActivator.retryActivation')}
        </Button>
      </React.Fragment>
    }}
  </Modal.Contents>;
}
export default BatchEvaluationActivator;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/BatchEvaluationActivator.jsx